'use client'

import React from 'react'
import {cn} from '../../../lib/utils'
import {Input} from '@fitro/customer-ui/Input'

interface PhoneDigitInputProps {
    value: string
    onChange: (digits: string) => void
    errorText?: string
    label?: string
    required?: boolean
    disabled?: boolean
    className?: string
    id?: string
}

function cleanDigits(raw: string): string {
    let digits = raw.replace(/\D/g, '')
    if (digits.startsWith('0048')) digits = digits.slice(4)
    else if (digits.startsWith('48') && digits.length > 9) digits = digits.slice(2)
    return digits.slice(0, 9)
}

function formatDigits(raw: string): string {
    return cleanDigits(raw).replace(/(\d{3})(?=\d)/g, '$1 ')
}

export const PhoneDigitInput = React.memo<PhoneDigitInputProps>(function PhoneDigitInput({
    value,
    onChange,
    className,
    ...props
}) {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const input = event.currentTarget
        const caret = input.selectionStart
        if (event.key !== 'Backspace' || caret === null || caret !== input.selectionEnd || input.value[caret - 1] !== ' ') return

        event.preventDefault()
        const removeIndex = cleanDigits(input.value.slice(0, caret)).length - 1
        const digits = cleanDigits(value)
        onChange(digits.slice(0, removeIndex) + digits.slice(removeIndex + 1))
        requestAnimationFrame(() => input.setSelectionRange(removeIndex, removeIndex))
    }

    return (
        <Input
            {...props}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={formatDigits(value)}
            onChange={(event) => onChange(cleanDigits(event.currentTarget.value))}
            onKeyDown={handleKeyDown}
            leftIcon={<span className="font-mono text-sm font-semibold text-gray-600 dark:text-gray-300">+48</span>}
            className={cn('pl-14', className)}
        />
    )
})
